import { useCallback, useMemo, useRef, useState } from 'react';
import {
  getDroppedDraft,
  getLaunchPlan,
  getProductionLaunch,
  replaceDraft,
  restoreDroppedDraft,
} from '@repo/front-end-services';
import type { DraftSwapOutcome } from '@repo/front-end-services';
import { useFloorplanStore } from '@repo/front-end-state';
import type {
  DroppedDraftSummary,
  FloorPlannerLayout,
  TwinLaunchSummary,
} from '@repo/frontend-types';

import type { DraftChoiceNotice, FirstMatchSource, ProductionRead } from './draftChoice';
import { asLoadedLaunch } from './draftChoice';
import { noticeText, whenText } from './draftChoiceText';

/**
 * The production read, the dropped-draft slot and the swaps (Load as Draft, Discard Draft,
 * Restore), as the onboarding page uses them (spec `twin-launch-history-draft-choice`, R4–R6; the
 * opening question it once owned was removed by `remove-draft-choice-modal`, Rob 2026-10-27). The
 * rules are in `draftChoice.ts`; this owns the reads and the swaps with their busy and error
 * states — so the page grows by the calls, not by another state machine.
 *
 * Three guarantees, each from a defect the review found:
 *
 * - **Stale answers are dropped.** Every read and swap captures a generation that {@link reset}
 *   bumps (a change of twin), and a result from an older generation writes nothing — so a slow read
 *   for the last twin can never put that twin's slot or launch on this one.
 * - **A swap that may have landed blocks saving until a reload** (`mustReload`). Neither swap is
 *   retried — a replayed *Discard Draft* would move the new draft into the slot, a replayed
 *   restore would undo itself — and a wizard left editable over a swap it cannot confirm would
 *   autosave its stale copy over the swapped-in one. (The ordinary plan save is version-checked now
 *   and blocks the same way on a refusal; a swap's own outcome is still unknown, so it blocks here.)
 * - **The dropped-draft read never holds the open** (Failure States): the plan hydrates at once,
 *   and the read fills Versions and the Discard confirmation when it settles.
 */

/** A saved plan as the resume read returns it. */
export interface ResumedPlan {
  id: number;
  name: string;
  layout: FloorPlannerLayout;
  /** `twin_plan.modified_at` EXACTLY as read (full precision; the swaps compare it). */
  modifiedAt: string | null;
  modifiedBy: string | null;
}

/** `refreshDropped`'s answer when the read failed: never confused with "no dropped draft". */
export const DROPPED_READ_FAILED = 'failed' as const;
export type DroppedRead = DroppedDraftSummary | null | typeof DROPPED_READ_FAILED;

/** Something a person reads, how it is toned, and whether the honest next step is a reload. */
export interface DraftChoiceError {
  message: string;
  reload: boolean;
  /** `info` for an empty answer (no slot, cleared by a launch); `error` for a failure. */
  tone: 'error' | 'info';
}

/** A polite announcement; `n` changes on every one, so the same words are announced again. */
export interface DraftChoiceAnnouncement {
  text: string;
  n: number;
}

/**
 * What the page does with a plan the open (or a swap) settled on. `modifiedBy` is who saved it,
 * when known: the resume read carries it; a swap leaves it out, because the swap stamped the person
 * pressing (the history panel's current-draft row reads it).
 */
export type HydratePlan = (
  plan: Omit<ResumedPlan, 'modifiedBy'> & { modifiedBy?: string | null },
  source: FirstMatchSource,
) => void;

/** The words a swap to a recorded launch says when it cannot complete, by who asked for it. */
const SWAP_TEXT = {
  production: {
    planUnread: "Couldn't load production's plan. Your draft is unchanged.",
    unknown: "We couldn't confirm the switch. Reload to see which version you have.",
  },
  load: {
    planUnread: "Couldn't load that launch's plan. Your draft is unchanged.",
    unknown: "We couldn't confirm the load. Reload to see which version you have.",
  },
} as const;

/**
 * What the history panel's *Load as draft* did (`twin-launch-history-panel`): `swapped`; `blocked`
 * (it may have landed, or the copy on screen may be stale — saving waits for a reload, and the
 * confirmation should close); `error` (nothing changed — `loadError` says why); `stale` (a change of
 * twin overtook it, or it never started).
 */
export type LoadLaunchResult = 'swapped' | 'blocked' | 'error' | 'stale';

const failure = (message: string, reload = false): DraftChoiceError => ({
  message,
  reload,
  tone: 'error',
});

/**
 * Refusals that mean the plan on the server may no longer be the one in the wizard (PR review,
 * 2026-10-06). After one of these, the wizard's copy can be stale, and the plan save has no
 * conflict check — so its next save could overwrite someone else's. They block until a reload
 * rather than leaving the stale copy editable. `launch_in_progress` and `slot_other_plan` change
 * nothing about this plan, and stay retryable in place.
 */
const STALE_AFTER: Record<'production' | 'restore', ReadonlySet<string>> = {
  production: new Set(['plan_changed', 'production_changed', 'not_found', 'plan_not_found']),
  restore: new Set(['plan_changed', 'slot_changed', 'superseded', 'not_found', 'plan_not_found']),
};

/** The blocking sentence for a refusal in `STALE_AFTER`: its own, ending in a reload. */
const staleMessage = (message: string): string =>
  /reload/i.test(message) ? message : `${message} Reload to see the latest.`;

const swapError = (
  outcome: Exclude<DraftSwapOutcome, { status: 'swapped' }>,
  what: 'production' | 'restore' | 'load',
): DraftChoiceError => {
  if (outcome.status === 'refused') {
    switch (outcome.reason) {
      case 'plan_changed':
        return failure(
          what === 'production'
            ? 'This draft was saved by someone else while you were deciding. Reload to see it.'
            : 'This changed while you were looking. Reload to see the latest.',
          true,
        );
      case 'slot_changed':
        return failure('This changed while you were looking. Reload to see the latest.', true);
      case 'launch_in_progress':
        return failure('A launch of this twin is in progress. Try again when it finishes.');
      case 'production_changed':
        return failure(
          'Production changed while you were deciding: a newer launch has completed. Reload to see it.',
          true,
        );
      case 'slot_other_plan':
        return failure(
          `Another of this twin's plans holds a dropped draft, which ${
            what === 'load' ? 'loading' : 'switching'
          } would lose. Your draft is unchanged.`,
        );
      case 'superseded':
        return { message: 'That draft was cleared by a launch.', reload: false, tone: 'info' };
      case 'not_found':
        return { message: "There's no dropped draft to restore.", reload: false, tone: 'info' };
      case 'plan_not_found':
        return failure('This plan no longer exists. Reload to see the latest.', true);
    }
  }
  // A layout the plan checks refuse (400) carries its own sentence; anything else definite — a
  // store refusal, or a failure before anything was written — is the spec's sentence.
  if (outcome.status === 'failed' && outcome.httpStatus === 400) return failure(outcome.error);
  return failure(
    what === 'production'
      ? "Couldn't switch to production. Your draft is unchanged."
      : what === 'load'
        ? "Couldn't load that launch. Your draft is unchanged."
        : "Couldn't restore the dropped draft. Nothing was changed.",
  );
};

/** What {@link useDraftChoice}'s shared swap did. */
type SwapToLaunchResult =
  /** Swapped and hydrated. */
  | { kind: 'swapped' }
  /** Nothing changed; say this where the person pressed. */
  | { kind: 'error'; error: DraftChoiceError }
  /** The swap may have landed, or the copy on screen may be stale: saving waits for a reload. */
  | { kind: 'blocked' }
  /** A change of twin overtook it; nothing to say. */
  | { kind: 'stale' };

export interface UseDraftChoiceParams {
  middlewareApi: string;
  accessToken: string | undefined;
  twinId: number | undefined;
  /** Puts a settled plan in the wizard. Read through a ref, so it may be defined after this hook. */
  hydrate: HydratePlan;
  /** Forget the live-configuration match history before a swapped plan is hydrated. */
  restartMatch: () => void;
  /**
   * The signed-in person's email, recorded as who started a draft loaded from an earlier launch
   * (`twin-details-page-version-changes`, R5.4). Read through a ref, like `hydrate`.
   */
  userEmail?: string | null;
  /**
   * Called after every swap that hydrated a new draft — Load, Discard, Restore — so
   * the page can remount whichever step is on screen from it (`twin-details-page-versions`, R8.4).
   * Read through a ref, like `hydrate`.
   */
  onSwapped?: () => void;
}

export const useDraftChoice = ({
  middlewareApi,
  accessToken,
  twinId,
  hydrate,
  restartMatch,
  userEmail,
  onSwapped,
}: UseDraftChoiceParams) => {
  const onSwappedRef = useRef(onSwapped);
  onSwappedRef.current = onSwapped;
  const hydrateRef = useRef(hydrate);
  hydrateRef.current = hydrate;
  const userEmailRef = useRef(userEmail);
  userEmailRef.current = userEmail;
  const restartMatchRef = useRef(restartMatch);
  restartMatchRef.current = restartMatch;

  /** Bumped by `reset` (a change of twin): any result from an older generation writes nothing. */
  const genRef = useRef(0);
  /** Set synchronously at the start of a swap, so a second press cannot start another. */
  const swapInFlightRef = useRef(false);
  /** A swap that may have landed and was not hydrated: saving is blocked until a reload. */
  const mustReloadRef = useRef(false);
  /** The sentence the reload block was raised with, so a save it refuses can say the TRUE reason. */
  const mustReloadMessageRef = useRef<string | null>(null);
  /** True while a swap runs or after one that could not be confirmed: the draft saver waits. */
  const isSavingBlocked = useCallback(() => swapInFlightRef.current || mustReloadRef.current, []);
  /** Why a save is refused right now: the reload block's own sentence, else a swap is running. */
  const savingBlockedReason = useCallback(
    () => mustReloadMessageRef.current ?? 'The draft is being switched. Nothing was saved.',
    [],
  );

  const [busy, setBusy] = useState<null | 'restore' | 'load'>(null);
  /** Why the history panel's last *Load as draft* changed nothing (`twin-launch-history-panel`). */
  const [loadError, setLoadError] = useState<DraftChoiceError | null>(null);
  const [dropped, setDropped] = useState<DroppedDraftSummary | null>(null);
  /** The last dropped-draft read failed: Twin Details says it could not check. */
  const [droppedReadFailed, setDroppedReadFailed] = useState(false);
  /**
   * A dropped-draft read is outstanding: the confirmations that replace the slot wait for it, so
   * their "this replaces the draft you dropped" line is never skipped (`remove-draft-choice-modal`
   * review — the removed opening question used to wait for it).
   */
  const [droppedChecking, setDroppedChecking] = useState(false);
  /** Bumped by every dropped-draft read: only the latest one's answer is shown. */
  const droppedReadRef = useRef(0);
  const [restoreError, setRestoreError] = useState<DraftChoiceError | null>(null);
  const [notice, setNoticeState] = useState<DraftChoiceNotice | null>(null);
  const [mustReload, setMustReload] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState<DraftChoiceAnnouncement>({ text: '', n: 0 });
  /**
   * The latest completed launch's id — the basis a first edit is stamped with (R1.2). Null when the
   * twin has none, or production could not be read (a deliberate under-claim).
   */
  const productionLaunchUuidRef = useRef<string | null>(null);

  const announce = useCallback((text: string) => {
    setAnnouncement(a => ({ text, n: a.n + 1 }));
  }, []);

  /** The swap may have landed and the wizard cannot show it: stop saving until a reload. */
  const blockUntilReload = useCallback((message: string) => {
    mustReloadRef.current = true;
    mustReloadMessageRef.current = message;
    setMustReload(message);
  }, []);

  /** The production read for the opening decision (R5.1). A missing twin id is a failed read. */
  const readProduction = useCallback(async (): Promise<
    Exclude<ProductionRead, { status: 'loading' }>
  > => {
    const gen = genRef.current;
    if (!accessToken || twinId === undefined) return { status: 'failed' };
    let outcome: Awaited<ReturnType<typeof getProductionLaunch>> | undefined;
    try {
      outcome = await getProductionLaunch(middlewareApi, accessToken, twinId);
    } catch {
      // The client reports failures as outcomes; anything thrown is still "could not find out".
      return { status: 'failed' };
    }
    if (outcome?.status !== 'found') return { status: 'failed' };
    if (gen === genRef.current) {
      productionLaunchUuidRef.current = outcome.production.launch?.launchUuid ?? null;
    }
    return { status: 'ready', ...outcome.production };
  }, [middlewareApi, accessToken, twinId]);

  /**
   * Read the plan's offered dropped draft and show it. A failed read offers nothing — never a wrong
   * offer — and a result for another twin (an older generation), or one a newer read has
   * overtaken (the open's read landing after a swap's), is dropped. The answer tells a failed read
   * apart from an empty slot, for the "couldn't check" warnings.
   */
  const refreshDropped = useCallback(
    async (planId: number | null): Promise<DroppedRead> => {
      const gen = genRef.current;
      const read = ++droppedReadRef.current;
      setDroppedChecking(true);
      let next: DroppedRead = null;
      if (accessToken && planId !== null) {
        try {
          const outcome = await getDroppedDraft(middlewareApi, accessToken, planId);
          next =
            outcome?.status === 'found'
              ? outcome.droppedDraft
              : outcome?.status === 'failed'
                ? DROPPED_READ_FAILED
                : null;
        } catch {
          next = DROPPED_READ_FAILED;
        }
      }
      if (gen === genRef.current && read === droppedReadRef.current) {
        setDropped(next === DROPPED_READ_FAILED ? null : next);
        setDroppedReadFailed(next === DROPPED_READ_FAILED);
        setDroppedChecking(false);
      }
      return next;
    },
    [middlewareApi, accessToken],
  );

  /**
   * Forget everything — a change of twin. Results still in flight for the old twin are dropped,
   * and a reload owed for an unconfirmed swap on the OLD twin's plan no longer blocks this one: the
   * new twin's plan is read afresh from the server.
   */
  const reset = useCallback(() => {
    genRef.current += 1;
    swapInFlightRef.current = false;
    mustReloadRef.current = false;
    mustReloadMessageRef.current = null;
    setMustReload(null);
    setBusy(null);
    setDropped(null);
    setDroppedReadFailed(false);
    setDroppedChecking(false);
    setRestoreError(null);
    setLoadError(null);
    setNoticeState(null);
    setAnnouncement(a => ({ text: '', n: a.n + 1 }));
    productionLaunchUuidRef.current = null;
  }, []);

  /**
   * Why the draft is shown as saved: on Twin Details AND announced politely — a region mounted with
   * its text already in it is not reliably announced.
   */
  const setNotice = useCallback(
    (next: DraftChoiceNotice | null) => {
      setNoticeState(next);
      if (next) announce(noticeText(next));
    },
    [announce],
  );

  /**
   * The one "put a recorded launch's plan in the draft" path, shared by Versions' *Discard Draft*
   * and *Load as draft* (`twin-launch-history-panel`, R4.6): read the launch's plan,
   * mark it (`asLoadedLaunch`), and swap it in through `replace-draft` with the stamp the caller
   * read and PRODUCTION's launch id — the database refuses if a newer launch has completed since.
   * The caller holds `swapInFlightRef` and the busy state.
   *
   * Never retried (a replay would move the loaded plan into the slot). A swap that may have landed,
   * or a refusal meaning the server's plan may be newer than the wizard's, blocks saving until a
   * reload. Anything else changed nothing and is returned for the caller to show.
   */
  const swapToLaunch = useCallback(
    async (args: {
      gen: number;
      plan: { id: number; name: string; modifiedAt: string | null };
      launch: TwinLaunchSummary;
      productionUuid: string;
      source: FirstMatchSource;
      what: 'production' | 'load';
      /**
       * The retired BIDs of the plan being REPLACED (`floor-plan-page-launch-bid-changes`, R10). The
       * caller supplies it, because only it knows where the current plan lives.
       */
      retiredEntityCodes: readonly string[] | null | undefined;
    }): Promise<SwapToLaunchResult> => {
      const { gen, plan, launch, productionUuid, source, what, retiredEntityCodes } = args;
      if (!accessToken || twinId === undefined) return { kind: 'stale' };
      const read = await getLaunchPlan(middlewareApi, accessToken, twinId, launch.launchUuid);
      if (gen !== genRef.current) return { kind: 'stale' };
      if (read.status === 'failed') {
        if (what === 'load') {
          console.warn('[launch-history] plan read failed', read.httpStatus ?? '(no status)');
        }
        // A listed launch whose plan is gone was pruned by a newer launch since the list was read.
        const gone =
          what === 'load' && read.httpStatus === 404
            ? ' It may have been removed by a newer launch. Reload to see the latest.'
            : '';
        return {
          kind: 'error',
          error: failure(`${SWAP_TEXT[what].planUnread}${gone}`, gone !== ''),
        };
      }
      const layout = asLoadedLaunch(read.launch.plan, {
        launchUuid: launch.launchUuid,
        productionUuid,
        nowIso: new Date().toISOString(),
        editedBy: userEmailRef.current ?? null,
        // Retired BIDs are about the twin, not the drawing: a loaded launch keeps the newer list.
        retiredEntityCodes,
      });
      const swap = await replaceDraft(middlewareApi, accessToken, plan.id, {
        layout,
        expectedModifiedAt: plan.modifiedAt,
        // Production as the person was shown it: refused if a newer launch has completed since.
        expectedLaunchUuid: productionUuid,
      });
      if (gen !== genRef.current) return { kind: 'stale' };
      if (swap.status === 'unknown') {
        blockUntilReload(SWAP_TEXT[what].unknown);
        return { kind: 'blocked' };
      }
      if (swap.status !== 'swapped') {
        const shown = swapError(swap, what);
        if (swap.status === 'refused' && STALE_AFTER.production.has(swap.reason)) {
          // The server may hold a newer plan than the wizard's (a newer launch also re-saves it),
          // and this wizard's copy is stale: nothing more is saved until a reload.
          blockUntilReload(staleMessage(shown.message));
          return { kind: 'blocked' };
        }
        return { kind: 'error', error: shown };
      }
      restartMatchRef.current();
      hydrateRef.current(
        { id: plan.id, name: plan.name, layout, modifiedAt: swap.response.modifiedAt },
        source,
      );
      onSwappedRef.current?.();
      return { kind: 'swapped' };
    },
    [accessToken, twinId, middlewareApi, blockUntilReload],
  );

  /**
   * The history panel's *Load as draft* (`twin-launch-history-panel`, R4): a recorded launch's
   * plan as the draft, the draft into the slot. `flush` saves any unsaved edit first — refused if
   * it cannot — and `currentStamp` is read AFTER it, as for a restore. An earlier launch loads with
   * its OWN observers (source `draft`, maria's ruling 2026-10-06); production loads with what is
   * running (source `running`). Resolves what happened ({@link LoadLaunchResult}) — never
   * read back from state, which a promise continuation sees before React has rendered it — so the
   * caller can close its confirmation and move focus. Refused while a reload is owed: after an
   * unknown outcome the swap is never sent again (it is not idempotent).
   */
  const loadLaunch = useCallback(
    async (args: {
      launch: TwinLaunchSummary;
      productionUuid: string;
      planId: number;
      planName: string;
      flush: () => Promise<boolean>;
      currentStamp: () => string | null;
      /**
       * Versions' *Discard Draft* (`twin-details-page-versions`, R8.2): production loaded as the
       * draft, said as a discard. The swap is the same one — production through this path.
       */
      intent?: 'discard';
    }): Promise<LoadLaunchResult> => {
      if (swapInFlightRef.current || mustReloadRef.current) return 'stale';
      if (!accessToken || twinId === undefined) return 'stale';
      const gen = genRef.current;
      const { launch, productionUuid } = args;
      swapInFlightRef.current = true;
      setBusy('load');
      setLoadError(null);
      try {
        if (!(await args.flush())) {
          if (gen === genRef.current) {
            setLoadError(
              failure(
                args.intent === 'discard'
                  ? "Your latest edits couldn't be saved, so nothing was swapped."
                  : "Your latest edits couldn't be saved, so nothing was loaded.",
              ),
            );
          }
          return 'error';
        }
        // The twin may have changed while the flush waited: never load into the old plan.
        if (gen !== genRef.current) return 'stale';
        const isProduction = launch.launchUuid === productionUuid;
        const result = await swapToLaunch({
          gen,
          plan: { id: args.planId, name: args.planName, modifiedAt: args.currentStamp() },
          launch,
          productionUuid,
          source: isProduction ? 'running' : 'draft',
          // Discard is the switch to production, and says so in its refusals (R8.2).
          what: args.intent === 'discard' ? 'production' : 'load',
          // The session is hydrated here, so the store holds the plan being replaced.
          retiredEntityCodes: useFloorplanStore.getState().layout?.retiredEntityCodes,
        });
        if (result.kind === 'error') {
          setLoadError(result.error);
          return 'error';
        }
        if (result.kind !== 'swapped') return result.kind;
        const when = whenText(launch.completedAt ?? launch.launchedAt);
        announce(
          args.intent === 'discard'
            ? "Draft discarded. It's kept as the dropped draft until the next launch."
            : isProduction
              ? `Now showing production, launched ${when}. Your draft was kept and can be restored.`
              : launch.launchNumber != null
                ? `v${launch.launchNumber} loaded as your draft. Your previous draft was kept and can be restored.`
                : `Now showing the launch of ${when} as your draft. Your previous draft was kept and can be restored.`,
        );
        await refreshDropped(args.planId);
        return 'swapped';
      } finally {
        if (gen === genRef.current) {
          swapInFlightRef.current = false;
          setBusy(null);
        }
      }
    },
    [accessToken, twinId, swapToLaunch, refreshDropped, announce],
  );

  const clearLoadError = useCallback(() => setLoadError(null), []);

  /**
   * *Restore dropped draft* (R4.4). `flush` saves any unsaved edit first and resolves whether it
   * landed; `currentStamp` is read AFTER it, so the swap compares the stamp the server now holds.
   * The busy state covers the flush too, so a second press cannot start a second restore.
   */
  const restore = useCallback(
    async (args: {
      planId: number;
      planName: string;
      flush: () => Promise<boolean>;
      currentStamp: () => string | null;
    }) => {
      if (!dropped || swapInFlightRef.current || !accessToken) return;
      const gen = genRef.current;
      const slot = dropped;
      swapInFlightRef.current = true;
      setBusy('restore');
      setRestoreError(null);
      try {
        if (!(await args.flush())) {
          if (gen === genRef.current) {
            setRestoreError(
              failure("Your latest edits couldn't be saved, so nothing was restored."),
            );
          }
          return;
        }
        // The twin may have changed while the flush waited: never send a restore for the old plan.
        if (gen !== genRef.current) return;
        // The plan being replaced may hold BIDs retired since the dropped draft was saved — a launch
        // that replaced the model and failed before completing (Copilot on #2115). Kept below.
        const keepRetired = useFloorplanStore.getState().layout?.retiredEntityCodes ?? [];
        const swap = await restoreDroppedDraft(middlewareApi, accessToken, args.planId, {
          expectedModifiedAt: args.currentStamp(),
          expectedDroppedAt: slot.droppedAt,
        });
        if (gen !== genRef.current) return;
        if (swap.status === 'unknown') {
          blockUntilReload(
            "We couldn't confirm the restore. Reload to see which version you have.",
          );
          return;
        }
        if (swap.status === 'refused' && STALE_AFTER.restore.has(swap.reason)) {
          // Someone else changed the plan or the slot meanwhile — another tab's restore, or a
          // launch that re-saved the plan. The wizard may hold a stale copy, and its next save
          // would overwrite theirs. Nothing more is saved until a reload.
          setDropped(null);
          blockUntilReload(staleMessage(swapError(swap, 'restore').message));
          return;
        }
        if (swap.status !== 'swapped') {
          const shown = swapError(swap, 'restore');
          setRestoreError(shown);
          if (shown.tone === 'info') setDropped(null);
          return;
        }
        // The answer carries the restored draft: hydrated from it, never from a second read that
        // could fail after the swap had committed.
        restartMatchRef.current();
        const restored = swap.response.layout;
        const retired = Array.from(
          new Set([...(restored?.retiredEntityCodes ?? []), ...keepRetired]),
        ).sort();
        const lost = retired.length > (restored?.retiredEntityCodes ?? []).length;
        hydrateRef.current(
          {
            id: args.planId,
            name: args.planName,
            layout: lost ? { ...restored, retiredEntityCodes: retired } : restored,
            modifiedAt: swap.response.modifiedAt,
          },
          'draft',
        );
        // The server copy is the restored one: mark the merged list unsaved, so the next save writes it.
        if (lost) useFloorplanStore.getState().setDirty(true);
        onSwappedRef.current?.();
        announce('Dropped draft restored.');
        await refreshDropped(args.planId);
      } finally {
        if (gen === genRef.current) {
          swapInFlightRef.current = false;
          setBusy(null);
        }
      }
    },
    [dropped, accessToken, middlewareApi, refreshDropped, announce, blockUntilReload],
  );

  return useMemo(
    () => ({
      busy,
      dropped,
      droppedReadFailed,
      droppedChecking,
      restoreError,
      loadError,
      notice,
      mustReload,
      announcement,
      productionLaunchUuidRef,
      isSavingBlocked,
      savingBlockedReason,
      blockUntilReload,
      readProduction,
      refreshDropped,
      reset,
      loadLaunch,
      clearLoadError,
      restore,
      setNotice,
    }),
    [
      busy,
      dropped,
      droppedReadFailed,
      droppedChecking,
      restoreError,
      loadError,
      notice,
      mustReload,
      announcement,
      isSavingBlocked,
      savingBlockedReason,
      blockUntilReload,
      readProduction,
      refreshDropped,
      reset,
      loadLaunch,
      clearLoadError,
      restore,
      setNotice,
    ],
  );
};

export type DraftChoice = ReturnType<typeof useDraftChoice>;
